import { useState, useEffect, useMemo } from 'react';
import { useStoredMarkets, type StoredMarket } from '../../hooks/useProperties';
import './MarketSidebar.css';

interface MarketSidebarProps {
  selectedMarkets: string[];
  onSelectionChange: (markets: string[]) => void;
}

interface GroupedMarkets {
  [state: string]: StoredMarket[];
}

type FilterMode = 'all' | 'personal' | 'investment';

const STATE_NAMES: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia',
  HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
  KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
  MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri',
  MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio',
  OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
  SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont',
  VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
  DC: 'District of Columbia', Other: 'Other',
};

// Score badge color based on score value
function getScoreBadgeClass(score?: number): string {
  if (!score) return 'score-na';
  if (score >= 90) return 'score-excellent';
  if (score >= 75) return 'score-good';
  if (score >= 60) return 'score-fair';
  return 'score-poor';
}

export function MarketSidebar({ selectedMarkets, onSelectionChange }: MarketSidebarProps) {
  const { markets, loading } = useStoredMarkets();
  const [collapsedStates, setCollapsedStates] = useState<string[]>([]);
  const [initialized, setInitialized] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterMode, setFilterMode] = useState<FilterMode>('all');

  // Filter markets based on search query and filter mode
  const filteredMarkets = useMemo(() => {
    let result = markets;

    // Search filter (ZIP or market name)
    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      const isZipSearch = /^\d{3,5}$/.test(query);

      if (isZipSearch) {
        // ZIP search - filter by market name containing the ZIP pattern
        // Since we don't have ZIPs per market in this view, search by name
        result = result.filter((m) =>
          m.name.toLowerCase().includes(query) ||
          m.market.includes(query)
        );
      } else {
        // Name search
        result = result.filter((m) =>
          m.name.toLowerCase().includes(query) ||
          m.market.includes(query)
        );
      }
    }

    // Investment vs Personal filter
    if (filterMode === 'investment') {
      // Investment: High landlord score (>=80) and good yield (>=10%)
      result = result.filter((m) =>
        (m.landlord_score ?? 0) >= 80 &&
        (m.gross_yield ?? 0) >= 0.10
      );
    } else if (filterMode === 'personal') {
      // Personal: Lower yield areas (likely HCOL, appreciation focus)
      result = result.filter((m) =>
        (m.gross_yield ?? 0) < 0.10 ||
        (m.zhvi_current ?? 0) > 300000
      );
    }

    return result;
  }, [markets, searchQuery, filterMode]);

  const groupedMarkets = filteredMarkets.reduce<GroupedMarkets>((acc, m) => {
    const state = m.state_code || 'Other';
    if (!acc[state]) acc[state] = [];
    acc[state].push(m);
    return acc;
  }, {});

  const sortedStates = Object.keys(groupedMarkets).sort((a, b) => {
    if (a === 'Other') return 1;
    if (b === 'Other') return -1;
    return (STATE_NAMES[a] || a).localeCompare(STATE_NAMES[b] || b);
  });

  // Initialize collapsed state - default all collapsed unless user has saved preference
  useEffect(() => {
    if (initialized || sortedStates.length === 0) return;

    const saved = localStorage.getItem('collapsedStates');
    if (saved) {
      setCollapsedStates(JSON.parse(saved));
    } else {
      // Default: all states collapsed
      setCollapsedStates([...sortedStates]);
    }
    setInitialized(true);
  }, [sortedStates, initialized]);

  const toggleState = (state: string) => {
    const newCollapsed = collapsedStates.includes(state)
      ? collapsedStates.filter((s) => s !== state)
      : [...collapsedStates, state];
    setCollapsedStates(newCollapsed);
    localStorage.setItem('collapsedStates', JSON.stringify(newCollapsed));
  };

  const toggleMarket = (slug: string, checked: boolean) => {
    const newSelection = checked
      ? [...selectedMarkets, slug]
      : selectedMarkets.filter((m) => m !== slug);
    onSelectionChange(newSelection);
    localStorage.setItem('selectedMarkets', JSON.stringify(newSelection));
  };

  const selectAll = () => {
    const allSlugs = filteredMarkets.map((m) => m.market);
    onSelectionChange(allSlugs);
    localStorage.setItem('selectedMarkets', JSON.stringify(allSlugs));
  };

  const deselectAll = () => {
    onSelectionChange([]);
    localStorage.setItem('selectedMarkets', JSON.stringify([]));
  };

  if (loading) {
    return (
      <div className="sidebar">
        <div className="sidebar-section">
          <div className="sidebar-title">Markets</div>
          <div className="loading-text">Loading markets...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="sidebar">
      <div className="sidebar-section">
        <div className="sidebar-title">Markets</div>

        {/* Search/Filter Input */}
        <div className="sidebar-search">
          <input
            type="text"
            placeholder="Search by ZIP or market..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        {/* Filter Mode Toggle */}
        <div className="filter-toggle">
          <button
            className={`filter-btn ${filterMode === 'all' ? 'active' : ''}`}
            onClick={() => setFilterMode('all')}
          >
            All
          </button>
          <button
            className={`filter-btn ${filterMode === 'investment' ? 'active' : ''}`}
            onClick={() => setFilterMode('investment')}
            title="High yield (10%+), landlord-friendly (80+ score)"
          >
            Investment
          </button>
          <button
            className={`filter-btn ${filterMode === 'personal' ? 'active' : ''}`}
            onClick={() => setFilterMode('personal')}
            title="Appreciation focus, higher value markets"
          >
            Personal
          </button>
        </div>

        <div className="sidebar-actions">
          <button className="sidebar-btn" onClick={selectAll}>Select All</button>
          <button className="sidebar-btn" onClick={deselectAll}>Clear</button>
        </div>

        {/* Results count */}
        <div className="filter-results">
          {filteredMarkets.length} of {markets.length} markets
        </div>

        <div className="market-list">
          {sortedStates.map((state) => {
            const stateMarkets = groupedMarkets[state];
            const isCollapsed = collapsedStates.includes(state);

            return (
              <div key={state} className="state-group">
                <div
                  className={`state-header ${isCollapsed ? 'collapsed' : ''}`}
                  onClick={() => toggleState(state)}
                >
                  <span className="arrow">&#9660;</span>
                  <span className="state-name">{STATE_NAMES[state] || state}</span>
                  <span className="state-count">{stateMarkets.length}</span>
                </div>
                <div className={`state-markets ${isCollapsed ? 'collapsed' : ''}`}>
                  <table className="market-table">
                    <tbody>
                      {stateMarkets.map((m) => (
                        <tr
                          key={m.market}
                          className={selectedMarkets.includes(m.market) ? 'selected' : ''}
                          onClick={() => toggleMarket(m.market, !selectedMarkets.includes(m.market))}
                        >
                          <td className="market-checkbox">
                            <input
                              type="checkbox"
                              checked={selectedMarkets.includes(m.market)}
                              onChange={(e) => {
                                e.stopPropagation();
                                toggleMarket(m.market, e.target.checked);
                              }}
                            />
                          </td>
                          <td className="market-name">{m.name}</td>
                          <td className="market-score">
                            <span className={`score-badge ${getScoreBadgeClass(m.landlord_score)}`}>
                              {m.landlord_score ?? '-'}
                            </span>
                          </td>
                          <td className="market-count">{m.count > 0 ? m.count : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
